import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import {
  MERCHANT_ENTERPRISE_SERVICE,
  fetchBrandVenueAvailaibilityDashboards,
  fetchEnterpriseVenueAvailabilityDashboards,
} from 'apis/enterprise/api';

import useTimedRefetch from './useTimedRefetch';
import { Entity } from '../types';

const useLookerDashboards = (entity: Entity) => {
  const intl = useIntl();
  const isEnterprise = entity.type === 'enterprise';
  const id = isEnterprise ? entity.enterprise.id : entity.brand.id;

  const { data, error, isLoading, refetch, remove } = useQuery({
    queryKey: [
      `${MERCHANT_ENTERPRISE_SERVICE}/${isEnterprise ? 'enterprises' : 'brands'}/${id}/venue-availability-dashboards`,
      intl.locale,
    ],
    queryFn: () =>
      isEnterprise
        ? fetchEnterpriseVenueAvailabilityDashboards(id, intl.locale)
        : fetchBrandVenueAvailaibilityDashboards(id, intl.locale),
    // When the user switches the menu item tab, it'll re-mount the component and reload the looker dashboards with
    // the same looker embed urls, which are not allowed by the looker embed mechanism we applied.
    // As a result, we need to refetch the new looker embed urls when the component is mounted each time.
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { refetchSoon, removeAndRefetch } = useTimedRefetch({
    data,
    remove,
    refetch,
  });

  return {
    data,
    error,
    isLoading,
    showLookerUrlAboutToExpireWarning: refetchSoon,
    refetchLookerUrls: removeAndRefetch,
  };
};

export default useLookerDashboards;
